
import { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useIntl } from "react-intl";

import CustomTable from 'components/table';
import { ActionMenu, CustomButton } from 'components';
import { masterPaginationServices, tableInitialState, } from 'utils/constants';
import { getTableParams } from 'utils/global';
import { RootReducerState } from 'utils/types';
import { subscriptionTab } from 'utils/constants/default';
import { getMasterPaginationData } from 'redux/actions';
import PageLayout from 'layouts/PageLayout';
import { subscriptionColumns } from 'utils/constants/tableColumn/column';
import { CustomTab } from 'components';
import Invoice from './Invoice';
import { Payment, RemoveRedEye } from '@mui/icons-material';
import { Chip } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const Account: React.FC = () => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const [status, setStatus] = useState('active');
    const [tableState, setTableState] = useState(tableInitialState);
    const [viewModal, setViewModal] = useState(false);
    const [selectedData, setSelectedData] = useState<any>(null);

    const { userData, loading } = useSelector(
        ({ masterPaginationReducer }: RootReducerState) => ({
            userData: masterPaginationReducer[masterPaginationServices.subscription].data,
            loading: masterPaginationReducer[masterPaginationServices.subscription].loading,
        }),
        shallowEqual
    );

    const { items, count } = userData;

    useEffect(() => {
        onApiCall();
    }, [tableState]);

    function onApiCall(sparam: string = status) {
        const params = getTableParams(tableState);
        // params.status = subscriptionTab.findIndex(x => x.value === sparam);
        params.status = sparam;
        dispatch(getMasterPaginationData(masterPaginationServices.subscription, params));
    }

    let columnAction = [
        {
            name: "id",
            label: formatMessage({ id: "expires_in" }),
            options: {
                customBodyRender: (value: number, tableMeta: any) => {
                    const rowData = items[tableMeta.rowIndex];
                    const now = dayjs();
                    const expiration = dayjs(rowData.ends_at);

                    if (!expiration.isValid() || !expiration.isAfter('2024', 'year')) {
                        return '-';
                    }

                    const diffInDays = expiration.diff(now, 'day');
                    const diffInHours = expiration.diff(now, 'hour');

                    if (diffInDays > 7) {
                        const weeks = Math.ceil(diffInDays / 7);
                        return `${weeks} week${weeks > 1 ? 's' : ''} left`;
                    } else if (diffInDays > 0) {
                        return `${diffInDays} day${diffInDays > 1 ? 's' : ''} left`;
                    } else if (diffInHours > 0) {
                        return `Less than a day left`;
                    } else {
                        return 'Expired';
                    }
                },
            },
        },
        {
            name: "status",
            label: formatMessage({ id: "status" }),
            options: {
                customBodyRender: (value: number, tableMeta: any) => {
                    const rowData = items[tableMeta.rowIndex];
                    let color = 'info';
                    return <Chip label={rowData?.status} size="small" color={color as any} variant="outlined" />
                },
            },
        },
        {
            label: formatMessage({ id: "action" }),
            name: "",
            options: {
                customBodyRender: (_: any, tableMeta: any) => {
                    const rowData = items[tableMeta.rowIndex];
                    return (
                        <ActionMenu>
                            <CustomButton
                                color='success'
                                size='small'
                                label="Invoices"
                                variant='outlined'
                                onClick={() => handleRowClick(rowData)}
                                startIcon={<Payment />}
                            />
                        </ActionMenu>
                    )
                }
            }
        }]

    function handleRowClick(rowData: any) {
        setSelectedData(rowData);
        setViewModal(true);
    }

    return (
        <PageLayout title={formatMessage({ id: "subscription" })} hideToolbar>
            <CustomTab
                tabs={subscriptionTab}
                onChange={(tab) => {
                    setStatus(tab);
                    onApiCall(tab);
                }}
                active={status}
            />
            <CustomTable
                columns={[...subscriptionColumns(formatMessage), ...columnAction]}
                tableData={items}
                tableState={tableState}
                itemsCount={count}
                setTableState={setTableState}
                loading={loading}
            />

            {viewModal && selectedData?.invoices && (
                <Invoice
                    open={viewModal}
                    onClose={() => setViewModal(false)}
                    tableData={selectedData?.invoices || []}
                />
            )}
        </PageLayout>
    )
}

export default Account;
